import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { crmPostventaV2Api } from "../crmpostventa-v2/crmPostventaV2Api";
import ProcessActionModal from "../crmpostventa-v2/components/ProcessActionModal";
import ProcessQueueTable from "../crmpostventa-v2/components/ProcessQueueTable";
import { getSlugByProcessCode } from "../crmpostventa-v2/processCatalog";
import BillingAlertsSection from "./billing-alerts/BillingAlertsSection";
import LostTerminalAlertsSection from "./lost-terminal-alerts/LostTerminalAlertsSection";
import OnboardingAlertsSection from "./onboarding-alerts/OnboardingAlertsSection";
import ProcessCardQueueSection from "./process-card-queue/ProcessCardQueueSection";
import ZocoCheckAlertsSection from "./zoco-check-alerts/ZocoCheckAlertsSection";

const defaultState = {
  loading: true,
  rows: [],
  total: 0,
  permissions: null,
  error: "",
};

const normalizeBlockError = (error) => {
  const message = String(error?.message || "").trim();
  if (message) {
    console.error("Detalle tecnico cola embebida:", message);
  }

  return "No se pudo cargar esta seccion.";
};

export default function EmbeddedProcessQueue({
  title,
  processCode,
  processSlug,
  viewAllPath,
  maxRows = 5,
  filters = {},
  emptyMessage,
  hideContainerCard = false,
  showTitle = true,
  showRefresh = true,
  showActions = true,
  className = "",
}) {
  const [state, setState] = useState(defaultState);
  const [detail, setDetail] = useState(null);
  const [existingEvidences, setExistingEvidences] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [responsables, setResponsables] = useState([]);
  const [initialResult, setInitialResult] = useState("");
  const [mutationNonce, setMutationNonce] = useState(0);

  const isBillingAlertsSection = processCode === "TERMINAL_SIN_FACTURACION_V2" && title === "Alertas de facturacion";
  const isProcessCardQueueSection =
    (processCode === "TERMINAL_SIN_FACTURACION_V2" && title === "Sin facturacion") ||
    (processCode === "RECUPERO_TERMINAL_V2" && title === "Recupero");
  const isLostTerminalAlertsSection = processCode === "TERMINAL_EXTRAVIADA_V2" && title === "Terminales extraviadas";
  const isOnboardingAlertsSection = processCode === "ONBOARDING_V2" && title === "Onboarding";
  const isZocoCheckAlertsSection = processCode === "ZOCO_CHECK_V2" && title === "Zoco Check";

  const requestParams = useMemo(
    () => ({
      procesoCodigo: processCode,
      fechaReferencia: filters.fechaReferencia,
      prioridad: filters.prioridad,
      estado: filters.estado,
      texto: filters.texto,
      pagina: 1,
      tamanoPagina: maxRows,
    }),
    [filters.estado, filters.fechaReferencia, filters.prioridad, filters.texto, maxRows, processCode]
  );

  const loadQueue = async () => {
    setState((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const response = await crmPostventaV2Api.obtenerAlertas(requestParams);
      setState({
        loading: false,
        rows: response?.filas || [],
        total: response?.total || 0,
        permissions: response?.permisos || null,
        error: "",
      });
      setResponsables(response?.responsables || []);
    } catch (error) {
      console.error(`Error cargando cola embebida ${processCode}`, error);
      setState({
        loading: false,
        rows: [],
        total: 0,
        permissions: null,
        error: normalizeBlockError(error),
      });
      setResponsables([]);
    }
  };

  useEffect(() => {
    loadQueue();
  }, [requestParams]);

  const openDetail = async (item, options = {}) => {
    try {
      setSubmitting(true);
      setInitialResult(options.initialResult || "");
      const resolvedSlug = item.procesoSlug || processSlug || getSlugByProcessCode(item.procesoCodigo || processCode);
      const [detailResponse, evidencesResponse] = await Promise.all([
        crmPostventaV2Api.obtenerDetalle(resolvedSlug, item.id),
        crmPostventaV2Api.obtenerEvidencias(item.id),
      ]);

      setDetail({
        ...detailResponse,
        instancia: {
          ...detailResponse?.instancia,
          procesoSlug: resolvedSlug,
        },
      });
      setExistingEvidences(evidencesResponse || []);
      setModalOpen(true);
    } catch (error) {
      Swal.fire(title, error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOwnershipAction = async (action, item, body = {}) => {
    try {
      setSubmitting(true);
      const versionToken = body.versionToken || item.versionToken;
      if (action === "take") {
        await crmPostventaV2Api.tomarProceso(item.id, {
          versionToken,
          motivo: body.motivo || "Toma desde tablero de alertas V2.",
        });
      } else if (action === "assign") {
        await crmPostventaV2Api.asignarProceso(item.id, {
          versionToken,
          usuarioId: body.usuarioId,
          motivo: body.motivo || "Asignacion desde tablero de alertas V2.",
        });
      } else if (action === "release") {
        await crmPostventaV2Api.liberarProceso(item.id, {
          versionToken,
          motivo: body.motivo || "Liberacion desde tablero de alertas V2.",
        });
      }

      await loadQueue();
      setMutationNonce((prev) => prev + 1);
    } catch (error) {
      Swal.fire(title, error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssign = async (item) => {
    const options = {};
    responsables.forEach((responsable) => {
      options[String(responsable.usuarioId)] = `${responsable.nombre} (${responsable.total})`;
    });

    if (!Object.keys(options).length) {
      Swal.fire(title, "Todavia no hay responsables visibles para asignar desde esta grilla.", "info");
      return;
    }

    const { isConfirmed, value } = await Swal.fire({
      title: "Asignar responsable",
      input: "select",
      inputOptions: options,
      inputPlaceholder: "Seleccionar responsable",
      showCancelButton: true,
      confirmButtonText: "Asignar",
      cancelButtonText: "Cancelar",
      inputValidator: (selected) => (!selected ? "Selecciona un responsable." : undefined),
    });

    if (!isConfirmed || !value) {
      return;
    }

    await handleOwnershipAction("assign", item, { usuarioId: Number(value) });
  };

  const handleRelease = async (item) => {
    const confirm = await Swal.fire({
      title: "Liberar proceso",
      text: "El proceso quedara nuevamente sin responsable.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Liberar",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    await handleOwnershipAction("release", item);
  };

  const handleSubmitAction = async (payload) => {
    if (!detail?.instancia?.id) {
      return;
    }

    try {
      setSubmitting(true);
      const response = await crmPostventaV2Api.ejecutarAccion(detail.instancia.procesoSlug, detail.instancia.id, payload);
      await Swal.fire(title, response?.message || "Accion registrada.", "success");
      setModalOpen(false);
      setDetail(null);
      setExistingEvidences([]);
      setInitialResult("");
      await loadQueue();
      setMutationNonce((prev) => prev + 1);
    } catch (error) {
      if (error?.status === 409) {
        const processSlug = detail.instancia.procesoSlug || getSlugByProcessCode(detail.instancia.procesoCodigo || processCode);
        const [detailResponse, evidencesResponse] = await Promise.all([
          crmPostventaV2Api.obtenerDetalle(processSlug, detail.instancia.id),
          crmPostventaV2Api.obtenerEvidencias(detail.instancia.id),
        ]);

        setDetail({
          ...detailResponse,
          instancia: {
            ...detailResponse?.instancia,
            procesoSlug: processSlug,
          },
        });
        setExistingEvidences(evidencesResponse || []);
        await loadQueue();
        await Swal.fire(title, "La gestión fue modificada por otro usuario. Actualizamos el detalle para que puedas intentar de nuevo.", "info");
        return;
      }

      Swal.fire(title, error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <div className={`crm-postventa-alert-queue ${className}`.trim()}>
      {showTitle && !isBillingAlertsSection && !isZocoCheckAlertsSection && !isOnboardingAlertsSection && !isLostTerminalAlertsSection ? (
        <div className="crm-alert-section-header">
          <div>
            <h3>{title}</h3>
          </div>
          <div className="crm-alert-accordion-head-badges">
            <span className="crm-alert-total-pill">{state.total || 0} alertas</span>
            {showRefresh ? (
              <Button variant="outline-secondary" size="sm" onClick={loadQueue} disabled={state.loading || submitting}>
                Actualizar
              </Button>
            ) : null}
            <Link className="btn btn-success btn-sm" to={viewAllPath}>
              Ver todos
            </Link>
          </div>
        </div>
      ) : null}

      {isBillingAlertsSection ? (
        <BillingAlertsSection
          title={title}
          processCode={processCode}
          processSlug={processSlug}
          globalFilters={filters}
          emptyMessage={emptyMessage}
          onManage={openDetail}
          onAssign={handleAssign}
          onRelease={handleRelease}
          onTake={(item) => handleOwnershipAction("take", item)}
          onMutationNonce={mutationNonce}
        />
      ) : isLostTerminalAlertsSection ? (
        <LostTerminalAlertsSection
          title={title}
          processCode={processCode}
          processSlug={processSlug}
          globalFilters={filters}
          emptyMessage={emptyMessage}
          onManage={openDetail}
          onAssign={handleAssign}
          onRelease={handleRelease}
          onTake={(item) => handleOwnershipAction("take", item)}
          onMutationNonce={mutationNonce}
        />
      ) : isOnboardingAlertsSection ? (
        <OnboardingAlertsSection
          title={title}
          processCode={processCode}
          processSlug={processSlug}
          globalFilters={filters}
          emptyMessage={emptyMessage}
          onManage={openDetail}
          onAssign={handleAssign}
          onRelease={handleRelease}
          onTake={(item) => handleOwnershipAction("take", item)}
          onMutationNonce={mutationNonce}
        />
      ) : isZocoCheckAlertsSection ? (
        <ZocoCheckAlertsSection
          title={title}
          processCode={processCode}
          processSlug={processSlug}
          globalFilters={filters}
          emptyMessage={emptyMessage}
          onManage={openDetail}
          onAssign={handleAssign}
          onRelease={handleRelease}
          onTake={(item) => handleOwnershipAction("take", item)}
          onMutationNonce={mutationNonce}
        />
      ) : isProcessCardQueueSection ? (
        <ProcessCardQueueSection
          rows={state.rows}
          loading={state.loading}
          emptyMessage={emptyMessage || `No hay registros activos para ${title.toLowerCase()}.`}
          detailLabel={title}
          searchPlaceholder="Buscar por comercio, CUIT, terminal, provincia..."
          onManage={openDetail}
          onTake={(item) => handleOwnershipAction("take", item)}
          onAssign={handleAssign}
          onRelease={handleRelease}
        />
      ) : state.loading ? (
        <div className="py-4 text-center">
          <Spinner animation="border" size="sm" className="me-2" />
          Cargando {title.toLowerCase()}...
        </div>
      ) : state.error ? (
        <Alert variant="warning" className="mb-0">
          {state.error}
        </Alert>
      ) : (
        <div className="crm-postventa-table-wrap-alertas">
          <div className="crm-postventa-table-scroll-x crm-postventa-table-scroll-alertas">
            <ProcessQueueTable
              rows={state.rows}
              onManage={openDetail}
              onTake={(item) => handleOwnershipAction("take", item)}
              onAssign={handleAssign}
              onRelease={handleRelease}
              loading={submitting}
              compact
              showActions={showActions}
              emptyMessage={emptyMessage || `No hay registros activos para ${title.toLowerCase()}.`}
              className="crm-postventa-table crm-postventa-table-alertas"
              containerClassName=""
            />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {hideContainerCard ? content : (
        <section className="crm-postventa-v2-panel p-4 crm-postventa-v2-embedded-queue">
          {content}
        </section>
      )}

      <ProcessActionModal
        show={modalOpen}
        onHide={() => {
          setModalOpen(false);
          setInitialResult("");
        }}
        detail={detail}
        existingEvidences={existingEvidences}
        submitting={submitting}
        initialResult={initialResult}
        onSubmit={handleSubmitAction}
      />
    </>
  );
}
